import React, { useContext, useEffect, useState } from 'react'
import Loading from '../compnents/Loading';
import { 
    Box, 
    Container, 
    Spinner, 
    Text, 
    VStack, 
    Input,
    Button,
    FormControl,
    FormLabel,
    FormHelperText,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Heading,
    InputGroup,
    InputRightElement,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import Navbar from '../compnents/Navbar';
import UploadYourFile from './UploadYourFile';
import SideBar from '../compnents/SideBar';
import Footer from '../compnents/Footer';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import EmptyStateCard from '../compnents/EmptyStateCard';

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV;

function Domain() {
    const { token, user, setUser, isAuthInitializing } = useContext(AuthContext);
    const navigate = useNavigate();
    
    // Subdomain states
    const [subdomain, setSubdomain] = useState('');
    const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
    const [availabilityStatus, setAvailabilityStatus] = useState(null); // null, 'available', 'unavailable'
    const [availabilityMessage, setAvailabilityMessage] = useState('');
    const [isRegistering, setIsRegistering] = useState(false);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const canRegisterSubdomain =
        Boolean(subdomain) &&
        availabilityStatus === 'available' &&
        !isCheckingAvailability &&
        !(user?.subdomain && user.subdomain !== '');

    // Set initial subdomain value if user already has one
    useEffect(() => {
        if (user && user.subdomain) {
            setSubdomain(user.subdomain);
            setRegistrationSuccess(true);
        }
    }, [user]);

    // Debounced availability check
    useEffect(() => {
        // Reset states when input changes
        setAvailabilityStatus(null);
        setAvailabilityMessage('');
        setErrorMessage('');

        // Don't check if subdomain is empty or user already has this subdomain
        if (!subdomain || subdomain.trim() === '' || (user && user.subdomain === subdomain)) {
            return;
        }

        // Debounce timer
        const timeoutId = setTimeout(() => {
            checkSubdomainAvailability(subdomain);
        }, 500); // Wait 500ms after user stops typing

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line
    }, [subdomain, user]);

    const checkSubdomainAvailability = async (subdomainToCheck) => {
        setIsCheckingAvailability(true);
        setAvailabilityStatus(null);
        setAvailabilityMessage('');

        try {
            const res = await axios.post(
                `${API_BASE_URL}/content/check-subdomain-availability`,
                { subdomain: subdomainToCheck },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (res.data.available) {
                setAvailabilityStatus('available');
                setAvailabilityMessage(res.data.message);
            } else {
                setAvailabilityStatus('unavailable');
                setAvailabilityMessage(res.data.message);
            }
        } catch (error) {
            console.error('Error checking subdomain availability:', error);
            setAvailabilityStatus('error');
            setAvailabilityMessage(error.response?.data?.message || 'Error checking availability');
        } finally {
            setIsCheckingAvailability(false);
        }
    };

    const handleRegisterSubdomain = async () => {
        setIsRegistering(true);
        setErrorMessage('');

        try {
            const res = await axios.post(
                `${API_BASE_URL}/content/register-subdomain`,
                { subdomain: subdomain },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Update user context with new subdomain
            setUser(prevUser => ({
                ...prevUser,
                subdomain: res.data.subdomain
            }));

            setRegistrationSuccess(true);
        } catch (error) {
            console.error('Error registering subdomain:', error);
            setErrorMessage(error.response?.data?.message || 'Error registering subdomain');
        } finally {
            setIsRegistering(false);
        }
    };

    const handleSubdomainChange = (e) => {
        const nextValue = e.target.value
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, '')
            .slice(0, 63);
        setSubdomain(nextValue);
    };

    if (isAuthInitializing || !token) {
        return (
            <Loading />
        );
    }

    if (!user) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <Spinner size="xl" color="brand.teal" />
            </Box>
        );
    }

    if (user && user.noOfFileUploads === 0) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg="brand.bg">
                <Navbar />
                <UploadYourFile setUser={setUser} token={token} />
            </Box>
        )
    }

    return (
        <Box minH="100vh" display="flex" flexDirection="column">
            <Navbar />
            <Box display="flex" flex="1">
                <SideBar />
                <Container maxW="container.lg" flex="1">
                    <VStack spacing={6} align="stretch" mt={5}>
                        {(['free', 'solo', 'pro'].includes(user.subscription) && (user.role === 'admin' || user.role === 'rm')) && (
                            <EmptyStateCard
                                icon={WarningIcon}
                                title={user.role === 'admin' ? 'Custom domains are included in Business' : 'Custom domains are configured by your admin'}
                                description={
                                    user.role === 'admin'
                                        ? 'Upgrade to the Business plan to publish a branded subdomain for your firm.'
                                        : 'Only an admin on the Business plan can register a custom subdomain for your workspace.'
                                }
                                hint={
                                    user.role === 'admin'
                                        ? 'Once upgraded, you can choose a branded URL and access your portal from that domain.'
                                        : 'If your team needs a branded URL, ask your admin to upgrade and complete domain setup.'
                                }
                                borderColor="brand.divider"
                                titleColor="gray.700"
                                descriptionColor="gray.600"
                                hintColor="gray.500"
                                iconColor="#0B2545"
                                maxW="full"
                            >
                                {user.role === 'admin' ? (
                                    <Button colorScheme="blue" bg="brand.navy" _hover={{ bg: 'brand.navy2' }} onClick={() => navigate('/subscription')}>
                                        View Business Plan
                                    </Button>
                                ) : (
                                    <Button variant="outline" borderColor="brand.teal" color="brand.navy" _hover={{ bg: 'brand.tealSoft' }} onClick={() => navigate('/dashboard')}>
                                        Back to Dashboard
                                    </Button>
                                )}
                            </EmptyStateCard>
                        )}
                        
                        {((user.subscription === 'business') && (user.role === 'admin')) && (
                            <Box 
                                p={8} 
                                bg="white" 
                                borderRadius="lg" 
                                boxShadow="md"
                                border="1px solid"
                                borderColor="gray.200"
                            >
                                <Heading as="h2" size="lg" color="brand.navy" mb={6}>
                                    Domain Management
                                </Heading>

                                {!registrationSuccess ? (
                                    <>
                                        <Text color="gray.600" mb={6}>
                                            Register your custom subdomain to access your personalized Finansee portal
                                        </Text>

                                        <FormControl mb={4}>
                                            <FormLabel color="gray.700" fontWeight="bold">
                                                Choose Your Subdomain
                                            </FormLabel>
                                            <Box
                                                mb={4}
                                                p={4}
                                                bg="brand.tealSoft"
                                                borderRadius="md"
                                                border="1px solid"
                                                borderColor="brand.divider"
                                            >
                                                <Text fontWeight="semibold" color="brand.navy2" mb={2}>
                                                    Before you register
                                                </Text>
                                                <VStack align="start" spacing={1}>
                                                    <Text fontSize="sm" color="brand.navy2">Use only lowercase letters, numbers, and hyphens.</Text>
                                                    <Text fontSize="sm" color="brand.navy2">Choose a name customers will recognize immediately.</Text>
                                                    <Text fontSize="sm" color="brand.navy2">Activation can take 2-4 hours after registration.</Text>
                                                </VStack>
                                            </Box>
                                            <InputGroup>
                                                <Input
                                                    value={subdomain}
                                                    onChange={handleSubdomainChange}
                                                    placeholder="your-company-name"
                                                    size="lg"
                                                    focusBorderColor="brand.teal"
                                                    disabled={user.subdomain && user.subdomain !== ''}
                                                />
                                                {isCheckingAvailability && (
                                                    <InputRightElement height="100%">
                                                        <Spinner size="sm" color="brand.teal" />
                                                    </InputRightElement>
                                                )}
                                                {!isCheckingAvailability && availabilityStatus === 'available' && (
                                                    <InputRightElement height="100%">
                                                        <CheckCircleIcon color="green.500" boxSize={5} />
                                                    </InputRightElement>
                                                )}
                                                {!isCheckingAvailability && (availabilityStatus === 'unavailable' || availabilityStatus === 'error') && (
                                                    <InputRightElement height="100%">
                                                        <WarningIcon color="red.500" boxSize={5} />
                                                    </InputRightElement>
                                                )}
                                            </InputGroup>
                                            <FormHelperText>
                                                Your subdomain will be: <strong>{subdomain || 'your-company-name'}.finansee.eu</strong>
                                            </FormHelperText>
                                            <FormHelperText color="gray.500" fontSize="sm">
                                                Use only lowercase letters, numbers, and hyphens (3-63 characters)
                                            </FormHelperText>
                                        </FormControl>

                                        {availabilityMessage && (
                                            <Alert 
                                                status={availabilityStatus === 'available' ? 'success' : 'error'} 
                                                mb={4}
                                                borderRadius="md"
                                            >
                                                <AlertIcon />
                                                <Text>{availabilityMessage}</Text>
                                            </Alert>
                                        )}

                                        {errorMessage && (
                                            <Alert status="error" mb={4} borderRadius="md">
                                                <AlertIcon />
                                                <Text>{errorMessage}</Text>
                                            </Alert>
                                        )}

                                        <Button
                                            colorScheme="blue"
                                            size="lg"
                                            width="100%"
                                            onClick={handleRegisterSubdomain}
                                            isLoading={isRegistering}
                                            isDisabled={!canRegisterSubdomain}
                                            loadingText="Registering..."
                                        >
                                            Register Subdomain
                                        </Button>

                                        {user.subdomain && user.subdomain !== '' && (
                                            <Alert status="info" mt={4} borderRadius="md">
                                                <AlertIcon />
                                                <Box>
                                                    <AlertTitle>Subdomain Already Registered</AlertTitle>
                                                    <AlertDescription>
                                                        You already have a subdomain registered. Please contact support to change it.
                                                    </AlertDescription>
                                                </Box>
                                            </Alert>
                                        )}
                                    </>
                                ) : (
                                    <Alert
                                        status="success"
                                        variant="subtle"
                                        flexDirection="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        textAlign="center"
                                        minHeight="300px"
                                        borderRadius="lg"
                                    >
                                        <AlertIcon boxSize="60px" mr={0} mb={4} />
                                        <AlertTitle mt={4} mb={2} fontSize="2xl">
                                            Subdomain Registered Successfully!
                                        </AlertTitle>
                                        <AlertDescription maxWidth="lg" fontSize="lg">
                                            <Text mb={4}>
                                                Your custom domain <strong>{subdomain}.finansee.eu</strong> has been registered.
                                            </Text>
                                            <Text color="gray.600">
                                                Your domain will be available within <strong>2-4 hours</strong> of processing time.
                                            </Text>
                                            <Text color="gray.600" mt={2}>
                                                You'll be able to access your portal at:
                                            </Text>
                                            <Text 
                                                color="brand.navy" 
                                                fontWeight="bold" 
                                                fontSize="xl" 
                                                mt={2}
                                                p={3}
                                                bg="brand.tealSoft"
                                                borderRadius="md"
                                            >
                                                {subdomain}.finansee.eu
                                            </Text>
                                        </AlertDescription>
                                    </Alert>
                                )}
                            </Box>
                        )}
                    </VStack>
                </Container>
            </Box>
            <Footer />
        </Box>
    )
}

export default Domain
